// ─── Deterministic seeded RNG ───────────────────────────────────────────────
// City content is a pure function of a fixed seed, so the same world
// coordinates always generate the same city (geo-anchored, stable, and safe
// for scenarios that target fixed infrastructure). Replaces Math.random().

function xmur3(str: string): () => number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^= h >>> 16) >>> 0;
  };
}

function mulberry32(a: number): () => number {
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

class Rng {
  private fn: () => number;
  constructor(seed: string) {
    const s = xmur3(seed);
    this.fn = mulberry32(s());
  }
  /** Float in [0, 1). */
  next(): number { return this.fn(); }
  /** Uniform pick from an array. */
  pick<T>(arr: T[]): T { return arr[Math.floor(this.fn() * arr.length)]; }
}

/** Default city seed — keeps the current world stable/reproducible. */
const DEFAULT_CITY_SEED = 'front-door-city-v1';

/** Chunk size in grid cells. The viewport streamer batches per chunk. */
export const CHUNK_CELLS = 16;

/** Integer chunk index for a (centered) grid coordinate. */
export function chunkIndexForGrid(gridX: number, gridZ: number): [number, number] {
  return [Math.floor(gridX / CHUNK_CELLS), Math.floor(gridZ / CHUNK_CELLS)];
}

/** Chunk map key for a (centered) grid coordinate. */
export function chunkKeyForGrid(gridX: number, gridZ: number): string {
  const [cx, cz] = chunkIndexForGrid(gridX, gridZ);
  return `${cx},${cz}`;
}

const ROADS: Record<string, string[]> = {
  'straight-h': ['road-straight', 'road-straight', 'road-straight', 'road-crossing'],
  'straight-v': ['road-straight', 'road-straight', 'road-straight', 'road-crossing'],
  crossroad: ['road-crossroad', 'road-crossroad', 'road-crossroad-line'],
  intersection: ['road-intersection', 'road-intersection'],
  bend: ['road-bend-sidewalk', 'road-bend-sidewalk', 'road-bend'],
};

// Footprint radius (in grid cells) each infra needs clear of roads.
// = ceil(nativeMaxXZ * scale / 2 / 30) + 1 buffer
// pump-station: 14.8*3/2/30 = 0.74 → ceil=1 +1 = 2
// factory: 20.5*2.2/2/30 = 0.75 → ceil=1 +1 = 2
// warehouse: 38*1.6/2/30 = 1.01 → ceil=2 +1 = 3
// office: 10.5*4/2/30 = 0.7 → ceil=1 +1 = 2
// cooling: 9*4/2/30 = 0.6 → ceil=1 +1 = 2
// gas-tank: 11.2*3.5/2/30 = 0.65 → ceil=1 +1 = 2
// water-tower: 6.2*5/2/30 = 0.52 → ceil=1 +1 = 2
// substation: 7.1*5/2/30 = 0.59 → ceil=1 +1 = 2
// pylon: 6*4/2/30 = 0.4 → ceil=1 +1 = 2
// excavator: 5.3*6/2/30 = 0.53 → ceil=1 +1 = 2
const INFRA_FOOTPRINT: Record<string, number> = {
  'pump-station': 2,
  'factory': 2,
  'warehouse': 3,
  'office-building': 2,
  'cooling-tower': 2,
  'gas-tank': 2,
  'water-tower': 2,
  'substation': 2,
  'pylon': 2,
  'excavator': 2,
  'work-barrier': 1,
};

const BUILDINGS = {
  commercial: [
    'commercial-building-a', 'commercial-building-b', 'commercial-building-c',
    'commercial-building-d', 'commercial-building-e', 'commercial-building-f',
    'commercial-building-g',
  ],
  skyscrapers: [
    'building-skyscraper-a', 'building-skyscraper-b', 'building-skyscraper-c',
    'building-skyscraper-d', 'building-skyscraper-e',
  ],
  lowDetail: [
    'low-detail-building-a', 'low-detail-building-b', 'low-detail-building-c',
    'low-detail-building-d', 'low-detail-building-e', 'low-detail-building-f',
  ],
  suburban: [
    'suburban-building-type-a', 'suburban-building-type-b', 'suburban-building-type-c',
    'suburban-building-type-d', 'suburban-building-type-e', 'suburban-building-type-f',
    'suburban-building-type-g', 'suburban-building-type-h', 'suburban-building-type-i',
    'suburban-building-type-j',
  ],
  industrial: [
    'industrial-building-a', 'industrial-building-b', 'industrial-building-c',
    'industrial-building-d', 'industrial-building-e', 'industrial-building-f',
  ],
};

// Varied vegetation from available models
const TREES_VARIED = [
  'tree-large', 'tree-small',
  'tree-beech', 'tree-birch', 'tree-conifer', 'tree-lime',
  'tree-oak', 'tree-poplar', 'tree-round', 'kenney-tree',
];

interface GridConfig {
  gridW: number;
  gridH: number;
  blockW: number;
  blockH: number;
  seed: string;
}

interface CityBlock {
  startX: number;
  startZ: number;
  endX: number;
  endZ: number;
  w: number;
  h: number;
}

interface RoadInfo {
  piece: string;
  rot: number;
}

export interface CityPlacement {
  modelId: string;
  gridX: number;
  gridZ: number;
  rotation: number;
  scale: number;
  category: string;
  label?: string;
  id?: string;
}

type ZoneType = 'downtown' | 'commercial' | 'suburban' | 'industrial';

class CityLayout {
  private cells: string[][];
  private blocks: CityBlock[] = [];
  private roadXPositions: number[] = [];
  private roadZPositions: number[] = [];

  constructor(private config: GridConfig, private rng: Rng) {
    this.cells = Array.from({ length: config.gridH }, () =>
      Array.from({ length: config.gridW }, () => 'empty')
    );
  }

  generate(): this {
    this.layRoads();
    this.identifyBlocks();
    return this;
  }

  getBlocks(): CityBlock[] { return this.blocks; }
  getCell(x: number, z: number): string { return this.cells[z]?.[x] ?? 'empty'; }
  getGridW(): number { return this.config.gridW; }
  getGridH(): number { return this.config.gridH; }

  private layRoads(): void {
    const { gridW, gridH, blockW, blockH } = this.config;
    const maxGap = blockW + 3;

    // Varied block widths: alternate between small and large blocks
    this.roadXPositions = [0];
    let x = 0;
    let wideNext = this.rng.next() > 0.5;
    while (true) {
      const gap = wideNext
        ? blockW + Math.floor(this.rng.next() * 3)       // large: 6-8
        : Math.max(3, blockW - 2 + Math.floor(this.rng.next() * 2)); // small: 4-5
      wideNext = !wideNext;
      x += gap + 1;
      if (x >= gridW - 1) break;
      this.roadXPositions.push(x);
    }
    // Add final edge road — but if remaining gap is too large, insert an extra road
    const lastX = this.roadXPositions[this.roadXPositions.length - 1];
    if (gridW - 1 - lastX > maxGap) {
      const mid = lastX + Math.floor((gridW - 1 - lastX) / 2);
      this.roadXPositions.push(mid);
    }
    this.roadXPositions.push(gridW - 1);

    // Varied block heights
    this.roadZPositions = [0];
    let z = 0;
    let tallNext = this.rng.next() > 0.5;
    while (true) {
      const gap = tallNext
        ? blockH + Math.floor(this.rng.next() * 3)       // large: 5-7
        : Math.max(3, blockH - 2 + Math.floor(this.rng.next() * 2)); // small: 3-4
      tallNext = !tallNext;
      z += gap + 1;
      if (z >= gridH - 1) break;
      this.roadZPositions.push(z);
    }
    // Same for Z — prevent oversized final block
    const lastZ = this.roadZPositions[this.roadZPositions.length - 1];
    if (gridH - 1 - lastZ > maxGap) {
      const mid = lastZ + Math.floor((gridH - 1 - lastZ) / 2);
      this.roadZPositions.push(mid);
    }
    this.roadZPositions.push(gridH - 1);

    const roadXSet = new Set(this.roadXPositions);
    const roadZSet = new Set(this.roadZPositions);

    for (let cz = 0; cz < gridH; cz++) {
      for (let cx = 0; cx < gridW; cx++) {
        if (roadXSet.has(cx) || roadZSet.has(cz)) {
          this.cells[cz][cx] = 'road';
        }
      }
    }
  }

  private identifyBlocks(): void {
    for (let i = 0; i < this.roadXPositions.length - 1; i++) {
      for (let j = 0; j < this.roadZPositions.length - 1; j++) {
        const startX = this.roadXPositions[i] + 1;
        const endX = this.roadXPositions[i + 1];
        const startZ = this.roadZPositions[j] + 1;
        const endZ = this.roadZPositions[j + 1];
        if (endX <= startX || endZ <= startZ) continue;
        this.blocks.push({ startX, startZ, endX, endZ, w: endX - startX, h: endZ - startZ });
      }
    }
  }

  getRoadPiece(x: number, z: number): RoadInfo | null {
    if (this.cells[z]?.[x] !== 'road') return null;

    const left = x > 0 && this.cells[z][x - 1] === 'road';
    const right = x < this.config.gridW - 1 && this.cells[z][x + 1] === 'road';
    const up = z > 0 && this.cells[z - 1][x] === 'road';
    const down = z < this.config.gridH - 1 && this.cells[z + 1][x] === 'road';

    const count = [left, right, up, down].filter(Boolean).length;

    if (count === 4) return { piece: 'crossroad', rot: 0 };

    if (count === 3) {
      if (!up) return { piece: 'intersection', rot: 0 };
      if (!left) return { piece: 'intersection', rot: Math.PI / 2 };
      if (!down) return { piece: 'intersection', rot: Math.PI };
      if (!right) return { piece: 'intersection', rot: -Math.PI / 2 };
    }

    if (count === 2) {
      if (left && right) return { piece: 'straight-h', rot: 0 };
      if (up && down) return { piece: 'straight-v', rot: Math.PI / 2 };
      if (left && down) return { piece: 'bend', rot: 0 };
      if (down && right) return { piece: 'bend', rot: Math.PI / 2 };
      if (right && up) return { piece: 'bend', rot: Math.PI };
      if (up && left) return { piece: 'bend', rot: -Math.PI / 2 };
    }

    return null;
  }
}

function getZone(gx: number, gz: number, cx: number, cz: number): ZoneType {
  const nx = (gx - cx) / cx;
  const nz = (gz - cz) / cz;
  const dist = Math.sqrt(nx * nx + nz * nz);

  if ((nx < -0.5 && nz < -0.3) || (nz > 0.6)) return 'industrial';
  if (dist < 0.35) return 'downtown';
  if (dist < 0.65) return 'commercial';
  return 'suburban';
}

function pickBuildingForZone(zone: ZoneType, rng: Rng): string {
  switch (zone) {
    case 'downtown':
      return rng.next() < 0.5 ? rng.pick(BUILDINGS.skyscrapers) : rng.pick(BUILDINGS.commercial);
    case 'commercial':
      return rng.next() < 0.3 ? rng.pick(BUILDINGS.lowDetail) : rng.pick(BUILDINGS.commercial);
    case 'suburban':
      return rng.pick(BUILDINGS.suburban);
    case 'industrial':
      return rng.pick(BUILDINGS.industrial);
  }
}

function getBuildingFacing(layout: CityLayout, x: number, z: number, rng: Rng): number {
  if (z > 0 && layout.getCell(x, z - 1) === 'road') return Math.PI;
  if (z < layout.getGridH() - 1 && layout.getCell(x, z + 1) === 'road') return 0;
  if (x > 0 && layout.getCell(x - 1, z) === 'road') return Math.PI / 2;
  if (x < layout.getGridW() - 1 && layout.getCell(x + 1, z) === 'road') return -Math.PI / 2;
  return rng.pick([0, Math.PI / 2, Math.PI, -Math.PI / 2]);
}

function isAdjacentToRoad(layout: CityLayout, x: number, z: number): boolean {
  return (
    layout.getCell(x - 1, z) === 'road' ||
    layout.getCell(x + 1, z) === 'road' ||
    layout.getCell(x, z - 1) === 'road' ||
    layout.getCell(x, z + 1) === 'road'
  );
}

function findNearestRoadFacing(layout: CityLayout, x: number, z: number, rng: Rng): number {
  // Search outward for the nearest road cell and face it
  for (let r = 1; r < 10; r++) {
    // Check cardinal directions first for clean facing
    if (layout.getCell(x, z - r) === 'road') return Math.PI;      // road above → face up
    if (layout.getCell(x, z + r) === 'road') return 0;            // road below → face down
    if (layout.getCell(x - r, z) === 'road') return Math.PI / 2;  // road left → face left
    if (layout.getCell(x + r, z) === 'road') return -Math.PI / 2; // road right → face right
  }
  return rng.pick([0, Math.PI / 2, Math.PI, -Math.PI / 2]);
}

export interface CityGenerationResult {
  placements: CityPlacement[];
  roadCells: Array<[number, number]>;
  gridW: number;
  gridH: number;
}

export function generateCity(config?: Partial<GridConfig>): CityGenerationResult {
  const cfg: GridConfig = {
    gridW: 80,
    gridH: 80,
    blockW: 6,
    blockH: 5,
    seed: DEFAULT_CITY_SEED,
    ...config,
  };

  const rng = new Rng(cfg.seed);
  const layout = new CityLayout(cfg, rng).generate();
  const placements: CityPlacement[] = [];
  const roadCells: Array<[number, number]> = [];

  const cx = cfg.gridW / 2;
  const cz = cfg.gridH / 2;

  // Roads
  for (let z = 0; z < cfg.gridH; z++) {
    for (let x = 0; x < cfg.gridW; x++) {
      const info = layout.getRoadPiece(x, z);
      if (!info) continue;
      const roadOptions = ROADS[info.piece];
      if (!roadOptions) continue;

      roadCells.push([x - cx, z - cz]);

      placements.push({
        modelId: rng.pick(roadOptions),
        gridX: x - cx,
        gridZ: z - cz,
        rotation: info.rot,
        scale: 1,
        category: 'roads',
      });
    }
  }

  // Infrastructure definitions — positions well inside grid bounds
  const infra: Array<{ id: string; modelId: string; gx: number; gz: number; label: string }> = [
    { id: 'control-centre', modelId: 'office-building', gx: -2, gz: -4, label: 'Skewb Control Centre' },
    { id: 'pump-beta', modelId: 'pump-station', gx: -25, gz: -12, label: 'Pumping Station Beta' },
    { id: 'cooling-b', modelId: 'cooling-tower', gx: -20, gz: -16, label: 'Cooling Tower B' },
    { id: 'gas-storage', modelId: 'gas-tank', gx: -27, gz: -18, label: 'Gas Storage Facility' },
    { id: 'water-treatment', modelId: 'factory', gx: -22, gz: -22, label: 'Water Treatment Works' },
    { id: 'pump-alpha', modelId: 'pump-station', gx: 18, gz: 19, label: 'Pumping Station Alpha' },
    { id: 'gas-reg', modelId: 'gas-tank', gx: 7, gz: 25, label: 'Gas Regulation Station' },
    { id: 'sub-south', modelId: 'substation', gx: 11, gz: 22, label: 'Grid Substation South' },
    { id: 'cooling-a', modelId: 'cooling-tower', gx: 22, gz: 22, label: 'Cooling Tower A' },
    { id: 'depot-west', modelId: 'warehouse', gx: -26, gz: 6, label: 'Operations Depot West' },
    { id: 'depot-east', modelId: 'warehouse', gx: 14, gz: -16, label: 'Operations Depot East' },
    { id: 'tower-north', modelId: 'water-tower', gx: 26, gz: -24, label: 'Water Tower (North)' },
    { id: 'tower-south', modelId: 'water-tower', gx: -18, gz: 28, label: 'Water Tower (South)' },
    { id: 'sub-north', modelId: 'substation', gx: 12, gz: -8, label: 'Grid Substation North' },
    // ── Scenario-target infra (Phase 0.5). Reuses already-registered infra
    //    models so scale/footprint/placement stay regression-safe; these
    //    just give scenario authors stable infraIds to aim cameras/effects.
    { id: 'mains-renewal-st', modelId: 'work-barrier', gx: -22, gz: 18, label: 'Mains Renewal — Cardwell St' },
    { id: 'mains-renewal-oak', modelId: 'work-barrier', gx: 16, gz: 22, label: 'Mains Renewal — Oak Lane' },
    { id: 'cso-river', modelId: 'pump-station', gx: 4, gz: -24, label: 'Combined Sewer Overflow' },
    { id: 'gas-governor', modelId: 'gas-tank', gx: 24, gz: 4, label: 'Gas Governor Station' },
    { id: 'training-centre', modelId: 'office-building', gx: -8, gz: -26, label: 'Workforce Training Centre' },
    { id: 'depot-south', modelId: 'warehouse', gx: 0, gz: 30, label: 'Operations Depot South' },
  ];

  const excavationSites = [
    { gx: 16, gz: 18 },
    { gx: -23, gz: -11 },
    { gx: 10, gz: -7 },
    { gx: -24, gz: 5 },
  ];

  // ─── PLACE INFRA FIRST so we know actual positions for building exclusion ───
  const infraPositions = new Set<string>();
  const placedInfraPositions = new Set<string>();

  for (const item of infra) {
    const footprint = INFRA_FOOTPRINT[item.modelId] ?? 2;
    let px = item.gx;
    let pz = item.gz;

    // All cells in footprint must be free of roads and previously placed infra
    const isClear = (testX: number, testZ: number): boolean => {
      for (let dx = -footprint; dx <= footprint; dx++) {
        for (let dz = -footprint; dz <= footprint; dz++) {
          if (layout.getCell(testX + cx + dx, testZ + cz + dz) === 'road') return false;
          if (placedInfraPositions.has(`${testX + dx},${testZ + dz}`)) return false;
        }
      }
      return true;
    };

    if (!isClear(px, pz)) {
      let found = false;
      for (let r = 1; r < 15 && !found; r++) {
        for (let dx = -r; dx <= r && !found; dx++) {
          for (let dz = -r; dz <= r && !found; dz++) {
            if (Math.abs(dx) !== r && Math.abs(dz) !== r) continue;
            if (isClear(px + dx, pz + dz)) {
              px += dx; pz += dz; found = true;
            }
          }
        }
      }
    }

    // Mark actual placed position (footprint + 1 buffer)
    for (let dx = -(footprint + 1); dx <= footprint + 1; dx++) {
      for (let dz = -(footprint + 1); dz <= footprint + 1; dz++) {
        placedInfraPositions.add(`${px + dx},${pz + dz}`);
        infraPositions.add(`${px + dx},${pz + dz}`);
      }
    }

    const facing = findNearestRoadFacing(layout, px + cx, pz + cz, rng);
    placements.push({
      modelId: item.modelId, gridX: px, gridZ: pz, rotation: facing,
      scale: 1, category: 'utility', label: item.label, id: item.id,
    });
  }

  // Excavation sites
  for (const site of excavationSites) {
    const excFp = INFRA_FOOTPRINT['excavator'] ?? 1;
    let ex = site.gx, ez = site.gz;

    const isExcClear = (tx: number, tz: number): boolean => {
      for (let dx = -excFp; dx <= excFp; dx++) {
        for (let dz = -excFp; dz <= excFp; dz++) {
          if (layout.getCell(tx + cx + dx, tz + cz + dz) === 'road') return false;
          if (placedInfraPositions.has(`${tx + dx},${tz + dz}`)) return false;
        }
      }
      return true;
    };

    if (!isExcClear(ex, ez)) {
      for (let r = 1; r < 8; r++) {
        let found = false;
        for (let dx = -r; dx <= r && !found; dx++) {
          for (let dz = -r; dz <= r && !found; dz++) {
            if (Math.abs(dx) !== r && Math.abs(dz) !== r) continue;
            if (isExcClear(ex + dx, ez + dz)) { ex += dx; ez += dz; found = true; }
          }
        }
        if (found) break;
      }
    }

    for (let dx = -(excFp + 1); dx <= excFp + 1; dx++) {
      for (let dz = -(excFp + 1); dz <= excFp + 1; dz++) {
        infraPositions.add(`${ex + dx},${ez + dz}`);
      }
    }

    placements.push({
      modelId: 'excavator', gridX: ex, gridZ: ez,
      rotation: rng.next() * Math.PI * 2, scale: 0.8, category: 'utility',
    });
  }

  // ─── BUILDINGS (placed AFTER infra, using actual infra positions) ───
  const buildingCells = new Set<string>();

  // Buildings per block
  for (const block of layout.getBlocks()) {
    const blockCX = block.startX + block.w / 2;
    const blockCZ = block.startZ + block.h / 2;
    const zone = getZone(blockCX, blockCZ, cx, cz);

    // Spacing: downtown every cell, commercial every 2, suburban/industrial every 2
    const spacing = zone === 'downtown' ? 1 : 2;
    // Skip chance varies by zone
    const skipChance = zone === 'downtown' ? 0.15 : zone === 'commercial' ? 0.2 : zone === 'suburban' ? 0.3 : 0.25;
    // Inset from road edge (1 cell = sidewalk area)
    const inset = 1;

    for (let bz = inset; bz < block.h - inset; bz += spacing) {
      for (let bx = inset; bx < block.w - inset; bx += spacing) {
        if (rng.next() < skipChance) continue;

        const worldX = block.startX + bx;
        const worldZ = block.startZ + bz;
        const gridKey = `${worldX - cx},${worldZ - cz}`;
        if (infraPositions.has(gridKey)) continue;

        const facing = getBuildingFacing(layout, worldX, worldZ, rng);
        const building = pickBuildingForZone(zone, rng);

        let cat = 'commercial';
        if (building.startsWith('suburban-')) cat = 'residential';
        else if (building.startsWith('industrial-')) cat = 'industrial';

        placements.push({
          modelId: building,
          gridX: worldX - cx,
          gridZ: worldZ - cz,
          rotation: facing + (rng.next() - 0.5) * 0.08,
          scale: 1,
          category: cat,
        });

        buildingCells.add(gridKey);
      }
    }
  }

  // Vegetation — varied trees, avoiding roads, buildings, and infra
  for (const block of layout.getBlocks()) {
    const blockCX = block.startX + block.w / 2;
    const blockCZ = block.startZ + block.h / 2;
    const zone = getZone(blockCX, blockCZ, cx, cz);

    // All zones get trees but at different densities
    const treeDensity = zone === 'downtown' ? 0.03
      : zone === 'commercial' ? 0.06
      : zone === 'suburban' ? 0.15
      : 0.08; // industrial

    for (let bz = 0; bz < block.h; bz++) {
      for (let bx = 0; bx < block.w; bx++) {
        if (rng.next() > treeDensity) continue;

        const worldX = block.startX + bx;
        const worldZ = block.startZ + bz;
        const gridKey = `${worldX - cx},${worldZ - cz}`;

        // Skip if occupied by building, infra, or on road
        if (buildingCells.has(gridKey)) continue;
        if (infraPositions.has(gridKey)) continue;
        if (layout.getCell(worldX, worldZ) === 'road') continue;

        // Skip cells adjacent to buildings (1 cell buffer)
        let nearBuilding = false;
        for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          if (buildingCells.has(`${worldX - cx + dx},${worldZ - cz + dz}`)) {
            nearBuilding = true;
            break;
          }
        }
        if (nearBuilding) continue;

        placements.push({
          modelId: rng.pick(TREES_VARIED),
          gridX: worldX - cx + (rng.next() - 0.5) * 0.4,
          gridZ: worldZ - cz + (rng.next() - 0.5) * 0.4,
          rotation: rng.next() * Math.PI * 2,
          scale: 0.8 + rng.next() * 0.4,
          category: 'nature',
        });
      }
    }
  }

  // Street trees along roads (every 3-4 tiles, on non-road perimeter cells)
  for (let z = 0; z < cfg.gridH; z++) {
    for (let x = 0; x < cfg.gridW; x++) {
      if (layout.getCell(x, z) === 'road') continue;
      if ((x + z) % 4 !== 0) continue;
      if (rng.next() > 0.4) continue;
      if (!isAdjacentToRoad(layout, x, z)) continue;

      const gridKey = `${x - cx},${z - cz}`;
      if (buildingCells.has(gridKey)) continue;
      if (infraPositions.has(gridKey)) continue;

      placements.push({
        modelId: rng.pick(TREES_VARIED),
        gridX: x - cx,
        gridZ: z - cz,
        rotation: rng.next() * Math.PI * 2,
        scale: 0.7 + rng.next() * 0.3,
        category: 'nature',
      });
    }
  }

  // Street lights at intersections and every 3rd straight tile
  for (let z = 0; z < cfg.gridH; z++) {
    for (let x = 0; x < cfg.gridW; x++) {
      const info = layout.getRoadPiece(x, z);
      if (!info) continue;
      const isJunction = info.piece === 'crossroad' || info.piece === 'intersection';
      const isStraight = info.piece.startsWith('straight') && (x + z) % 4 === 0;
      if (!isJunction && !isStraight) continue;

      placements.push({
        modelId: 'light-curved',
        gridX: x - cx + 0.4,
        gridZ: z - cz + 0.4,
        rotation: info.rot,
        scale: 1,
        category: 'roads',
      });
    }
  }

  return { placements, roadCells, gridW: cfg.gridW, gridH: cfg.gridH };
}
